import { useState, useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { intl } from '../../../../../../../i18n'
import { useVersionReportVersionRepository } from '../../../../../../../services/adaptors/versionReportVersionRepositoryAdaptor'
import {
  addGlobalMessage,
  MessageLevel,
} from '../../../../../../../store/messages'
import { VersionReportVersionEntity } from '../../../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { getErrorCode } from '../../../../../../../infrastructure/api/error'

export type UseReportDialogProps = {
  onAfterSubmit: () => void | Promise<void>
  onClose: () => void
  original: VersionReportVersionEntity
}

export const useReportDialog = ({
  onAfterSubmit,
  onClose,
  original,
}: UseReportDialogProps) => {
  const [reportName, setReportName] = useState('')
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()
  const { updateVersionName } = useVersionReportVersionRepository()

  useEffect(() => {
    setReportName(original.name)
  }, [original])

  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true)
      const trimmedName = reportName.trim()
      await updateVersionName({
        uuid: original.uuid,
        lockVersion: original.lockVersion,
        name: trimmedName,
      })
      dispatch(
        addGlobalMessage({
          type: MessageLevel.SUCCESS,
          title: intl.formatMessage({ id: 'update.completed.title' }),
          text: `報告書「${trimmedName}」が更新されました`,
        })
      )
      await onAfterSubmit()
      onClose()
    } catch (error) {
      if (getErrorCode(error) === 'CONSISTENCY_VIOLATION') {
        dispatch(
          addGlobalMessage({
            type: MessageLevel.WARN,
            title: intl.formatMessage({ id: 'versionReport.conflict.title' }),
            text: intl.formatMessage({ id: 'versionReport.conflict.message' }),
          })
        )
        await onAfterSubmit()
        onClose()
        return
      }
      console.error('Report rename failed:', error)
      dispatch(
        addGlobalMessage({
          type: MessageLevel.ERROR,
          title: '報告書更新エラー',
          text: '報告書名の更新に失敗しました',
        })
      )
    } finally {
      setLoading(false)
    }
  }, [
    original,
    reportName,
    updateVersionName,
    dispatch,
    onAfterSubmit,
    onClose,
  ])

  const handleClose = useCallback(() => {
    if (!loading) {
      onClose()
    }
  }, [loading, onClose])

  return {
    reportName,
    setReportName,
    loading,
    handleSubmit,
    handleClose,
  }
}
